import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/lib/api";
import { NotFound } from "@/pages/SimplePages";
import NAVIGATION from "@/data/navigation.json";
import { corpsHtml } from "@/lib/articleCorps.mjs";
import "@/styles/article-corps.css";

export function JournalPage() {
  const { data } = useContent();
  const s = data?.settings || {};
  const posts = data?.posts || [];
  if (!data) return null;
  return (
    <main className="wrap" style={{ paddingTop: 160, paddingBottom: 96 }} data-testid="journal-page">
      <Seo
        title="Journal — production, média, IA | SLIZ"
        description="Analyses SLIZ : production audiovisuelle, achat d'espace social, DOOH et TV, IA générative en production vidéo."
        path="/journal/"
      />
      <p className="t-label" style={{ marginBottom: 20 }}>Journal</p>
      <h1 className="t-h1" style={{ margin: 0 }}>Production, média, IA.</h1>
      <p className="t-chapo" style={{ marginTop: 22, maxWidth: 640 }}>Ce qu'on apprend en produisant et en diffusant, partagé sans jargon.</p>
      <div style={{ marginTop: 64, borderTop: "1px solid var(--li)" }}>
        {posts.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.05}>
            <Link to={`/journal/${p.slug}/`} className="exp-row" style={{ padding: "34px 0" }} data-testid={`journal-${p.slug}`}>
              <span>
                <span className="t-label" style={{ display: "block", marginBottom: 10 }}>{p.date}</span>
                <span className="nom" style={{ fontSize: "clamp(19px,2vw,26px)", fontVariationSettings: '"wdth" 106, "wght" 700', letterSpacing: "-0.02em" }}>{p.titre}</span>
                <span className="t-body" style={{ display: "block", marginTop: 10, maxWidth: 640, fontSize: 15 }}>{p.excerpt}</span>
              </span>
              <span className="det">Lire</span>
            </Link>
          </Reveal>
        ))}
      </div>
    </main>
  );
}

export function ArticlePage({ slug }) {
  const { data } = useContent();
  const s = data?.settings || {};
  const post = (data?.posts || []).find((p) => p.slug === slug);
  if (!data) return null;
  if (!post) return <NotFound />;
  const linked = (post.services || []).map((sl) => (data.services || []).find((x) => x.slug === sl)).filter(Boolean);
  return (
    <main className="wrap" style={{ paddingTop: 160, paddingBottom: 96 }} data-testid={`article-${post.slug}`}>
      <Seo
        title={`${post.titre} | SLIZ`}
        description={post.excerpt}
        path={`/journal/${post.slug}/`}
      />
      <p className="t-label" style={{ marginBottom: 20 }}><Link to="/journal/" style={{ color: "var(--ink3)" }}>Journal</Link> — {post.date}</p>
      <h1 className="t-h1" style={{ margin: 0, fontSize: "clamp(32px,4.6vw,64px)", maxWidth: 1000 }}>{post.titre}</h1>
      <p className="t-chapo" style={{ marginTop: 24, maxWidth: 720 }}>{post.excerpt}</p>
      <div style={{ maxWidth: 800 }}>
        {post.sections.map((sec, i) => (
          <section key={i} style={{ borderTop: "1px solid var(--li)", marginTop: 48, paddingTop: 36 }}>
            <h2 className="t-h3" style={{ margin: "0 0 18px", fontSize: 26 }}>{sec.h2}</h2>
            {/* Même fabricant que le HTML servi : texte, bouton et tableau sortent de corpsHtml. */}
            <div dangerouslySetInnerHTML={{ __html: corpsHtml(sec.body, NAVIGATION.ctas_guide, true) }} />
          </section>
        ))}
      </div>
      {linked.length > 0 && (
        <nav style={{ marginTop: 72, borderTop: "1px solid var(--li)", paddingTop: 32 }} aria-label="Services liés">
          <p className="t-label" style={{ marginBottom: 20 }}>Pour aller plus loin</p>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            {linked.map((x) => (
              <Link key={x.slug} to={`/${x.slug}/`} className="link-arrow" data-testid={`article-link-${x.slug}`}>{x.h1} <ArrowRight size={13} strokeWidth={1.5} /></Link>
            ))}
          </div>
        </nav>
      )}
      <p className="t-body" style={{ marginTop: 72, maxWidth: 760 }} data-testid={NAVIGATION.appel_estimer.testid}>
        {NAVIGATION.appel_estimer.avant}
        <a href={NAVIGATION.appel_estimer.to}>{NAVIGATION.appel_estimer.mot}</a>
        {NAVIGATION.appel_estimer.apres}
      </p>
    </main>
  );
}
